import React, { useContext, useEffect, useMemo, useState } from "react";
import "./Report.css";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import loader from "../../../../assets/Image/loader.gif";
import LoadingOverlay from "../../../../components/common/LoadingBar";
import useFormattingUtils from "../../../../context/useFormattingUtils";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const getNumber = (value) => {
  const number = parseFloat(value);
  return Number.isNaN(number) ? 0 : number;
};

const getArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const csvEscape = (value) => {
  const text = value === undefined || value === null ? "" : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, rows) => {
  const csvContent = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getPaymentMethod = (item) =>
  String(item?.pay_method || item?.type || item?.payment_method || "other").toLowerCase();

const getDetailAmount = (item) =>
  getNumber(
    item?.amount ||
      item?.payable_amount ||
      item?.total_amount ||
      item?.invoice_amounts ||
      item?.invoice_amount ||
      item?.total_sale ||
      item?.total_online_net_sales
  );

const NoRecords = ({ text = "No records available" }) => (
  <div className="pvr-empty-state">{text}</div>
);

const PaymentTags = ({ data = [], amountKey = "amount", methodKey = "pay_method" }) => {
  const { formatCurrency } = useFormattingUtils();

  const totals = data.reduce(
    (acc, item) => {
      const rawMethod = item?.[methodKey] || item?.type || "other";
      const method = String(rawMethod).toLowerCase();
      const amount = getNumber(item?.[amountKey]);

      if (method.includes("cash")) acc.cash += amount;
      else if (method.includes("cheque")) acc.cheque += amount;
      else if (method.includes("bank") || method.includes("card")) acc.bank += amount;
      else acc.other += amount;

      return acc;
    },
    { cash: 0, cheque: 0, bank: 0, other: 0 }
  );

  return (
    <div className="pvr-payment-tags">
      <span className="pvr-payment-tag pvr-cash">Cash: {formatCurrency(totals.cash.toFixed(2))}</span>
      <span className="pvr-payment-tag pvr-cheque">Cheque: {formatCurrency(totals.cheque.toFixed(2))}</span>
      <span className="pvr-payment-tag pvr-bank">Bank/Card: {formatCurrency(totals.bank.toFixed(2))}</span>
      {totals.other > 0 && (
        <span className="pvr-payment-tag pvr-other">Other: {formatCurrency(totals.other.toFixed(2))}</span>
      )}
    </div>
  );
};

const DetailTable = ({ columns = [], data = [] }) => {
  if (!data || data.length === 0) {
    return <NoRecords />;
  }

  return (
    <div className="pvr-detail-table-wrap">
      <table className="pvr-detail-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item?.id || item?.invoice_no || item?.invoiceNumber || index}>
              {columns.map((column) => (
                <td key={column.key}>{column.render ? column.render(item, index) : item?.[column.key] || "N/A"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DebitDetails = ({ data = [] }) => {
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className="pvr-details-card">
      <PaymentTags data={data} amountKey="amount" methodKey="type" />
      <DetailTable
        data={data}
        columns={[
          { key: "invoiceNumber", header: "Invoice No" },
          { key: "vendorName", header: "Vendor / Department" },
          {
            key: "amount",
            header: "Amount",
            render: (item) => formatCurrency(getNumber(item.amount).toFixed(2)),
          },
          { key: "type", header: "Mode" },
          { key: "date", header: "Date" },
        ]}
      />
    </div>
  );
};

const GasPurchase = ({ data = [] }) => {
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className="pvr-details-card">
      <DetailTable
        data={data}
        columns={[
          { key: "invoice_no", header: "Invoice No" },
          {
            key: "invoice_amounts",
            header: "Amount",
            render: (item) => formatCurrency(getNumber(item.invoice_amounts || item.amount).toFixed(2)),
          },
          { key: "type", header: "Mode" },
          { key: "date", header: "Date" },
        ]}
      />
    </div>
  );
};

const CreditInvoiceDetails = ({ data = [] }) => {
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className="pvr-details-card">
      <DetailTable
        data={data}
        columns={[
          { key: "date", header: "Date" },
          {
            key: "total_amount",
            header: "Total Amount",
            render: (item) => formatCurrency(getNumber(item.total_amount).toFixed(2)),
          },
        ]}
      />
    </div>
  );
};

const PayrollDetails = ({ data = [] }) => {
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className="pvr-details-card">
      <PaymentTags data={data} amountKey="total_amount" methodKey="pay_method" />
      <DetailTable
        data={data}
        columns={[
          { key: "payroll_employee_name", header: "Employee" },
          { key: "salary_unit", header: "Salary Unit" },
          {
            key: "total_amount",
            header: "Total Amount",
            render: (item) => formatCurrency(getNumber(item.total_amount).toFixed(2)),
          },
          { key: "pay_method", header: "Mode" },
          { key: "payroll_date", header: "Date" },
        ]}
      />
    </div>
  );
};

const POSAppInvoiceDetails = ({ data = [] }) => {
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className="pvr-details-card">
      <PaymentTags data={data} amountKey="payable_amount" methodKey="pay_method" />
      <DetailTable
        data={data}
        columns={[
          { key: "invoice_no", header: "POS Invoice No" },
          { key: "date", header: "Date" },
          {
            key: "total_qty",
            header: "Qty",
            render: (item) => item.total_qty || item.qty || "0",
          },
          {
            key: "payable_amount",
            header: "Collected Amount",
            render: (item) => formatCurrency(getNumber(item.payable_amount || item.total_amount).toFixed(2)),
          },
          {
            key: "pay_method",
            header: "Mode",
            render: (item) => item.pay_method || "N/A",
          },
        ]}
      />
    </div>
  );
};

const SalesDetails = ({ data = [], type = "Sales" }) => {
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className="pvr-details-card">
      <PaymentTags data={data} amountKey="payable_amount" methodKey="pay_method" />
      <DetailTable
        data={data}
        columns={[
          { key: "invoice_no", header: "Invoice No" },
          {
            key: "customer",
            header: type === "Wholesale Sales" ? "Customer" : "Customer / Type",
            render: (item) => item.customer || item.customer_name || item.invoice_type || "N/A",
          },
          { key: "date", header: "Date" },
          {
            key: "payable_amount",
            header: "Received Amount",
            render: (item) => formatCurrency(getNumber(item.payable_amount || item.total_amount).toFixed(2)),
          },
          {
            key: "pay_method",
            header: "Mode",
            render: (item) => item.pay_method || "N/A",
          },
        ]}
      />
    </div>
  );
};

const LotterySalesDetails = ({ data = [] }) => {
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className="pvr-details-card">
      <DetailTable
        data={data}
        columns={[
          { key: "date", header: "Date" },
          {
            key: "total_online_net_sales",
            header: "Online Net Sales",
            render: (item) => formatCurrency(getNumber(item.total_online_net_sales).toFixed(2)),
          },
          {
            key: "instant_sale_sr34",
            header: "Instant Sale SR34",
            render: (item) => formatCurrency(getNumber(item.instant_sale_sr34).toFixed(2)),
          },
          {
            key: "total_sale",
            header: "Total Sale",
            render: (item) => formatCurrency(getNumber(item.total_sale).toFixed(2)),
          },
        ]}
      />
    </div>
  );
};

const CollapsibleItem = ({ title, amount, details, type = "neutral" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { formatCurrency } = useFormattingUtils();

  return (
    <div className={`pvr-collapsible-item ${isExpanded ? "pvr-expanded" : ""}`}>
      <button
        type="button"
        className={`pvr-collapsible-trigger pvr-${type}`}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div>
          <div className="pvr-collapsible-title">{title}</div>
          <div className="pvr-collapsible-subtitle">
            {isExpanded ? "Hide transaction details" : "View transaction details"}
          </div>
        </div>

        <div className="pvr-collapsible-amount">
          <span>{formatCurrency(getNumber(amount).toFixed(2))}</span>
          <span className="pvr-collapsible-icon">{isExpanded ? "−" : "+"}</span>
        </div>
      </button>

      {isExpanded && <div className="pvr-collapsible-body">{details}</div>}
    </div>
  );
};

const ReportGraphs = ({ debitData, creditData }) => {
  const { formatCurrency } = useFormattingUtils();

  const debitChartData = debitData.debit.items.map((item) => ({
    name: item.title,
    amount: Number(item.amount || 0),
  }));

  const creditChartData = creditData.credit.items.map((item) => ({
    name: item.title,
    amount: Number(item.amount || 0),
  }));

  const summaryData = [
    {
      name: "Debit",
      amount: Number(debitData.debit.total || 0),
      color: "#ef4444",
    },
    {
      name: "Credit",
      amount: Number(creditData.credit.total || 0),
      color: "#10b981",
    },
  ];

  const tooltipFormatter = (value) => formatCurrency(Number(value || 0).toFixed(2));

  return (
    <div className="pvr-graphs-wrapper">
      <div className="pvr-graph-card">
        <div className="pvr-graph-title">Debit Breakdown</div>
        <ResponsiveContainer width="100%" height={270}>
          <BarChart data={debitChartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={tooltipFormatter} />
            <Bar dataKey="amount" fill="#ef4444" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="pvr-graph-card">
        <div className="pvr-graph-title">Credit Breakdown</div>
        <ResponsiveContainer width="100%" height={270}>
          <BarChart data={creditChartData}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={tooltipFormatter} />
            <Bar dataKey="amount" fill="#10b981" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="pvr-graph-card">
        <div className="pvr-graph-title">Debit vs Credit</div>
        <ResponsiveContainer width="100%" height={270}>
          <PieChart>
            <Pie
              data={summaryData}
              dataKey="amount"
              nameKey="name"
              outerRadius={86}
              innerRadius={48}
              paddingAngle={3}
              label
            >
              {summaryData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={tooltipFormatter} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

function ReportComponent() {
  const { Post } = useApi();
  const { user } = useContext(AuthContext);
  const { formatCurrency } = useFormattingUtils();

  const currentYear = new Date().getFullYear();

  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reportData, setReportData] = useState(null);
  const [creditReportData, setCreditReportData] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    reportType: "business",
    partyType: "all",
    partyQuery: "",
    side: "all",
    includeDetails: true,
  });

  const formatInvoices = (invoiceArray = [], category = "Purchase or Expense") => {
    return getArray(invoiceArray).map((inv) => ({
      invoiceNumber: inv.invoice_no || "N/A",
      vendorName: inv.vendor_department_name || inv.vendor_name || "Unknown",
      category,
      amount: getNumber(inv.invoice_amount || inv.amount),
      type: inv.pay_method
        ? String(inv.pay_method).charAt(0).toUpperCase() + String(inv.pay_method).slice(1)
        : "Other",
      date: inv.date || "N/A",
    }));
  };

  const formatPayrolls = (payrollArray = []) => {
    return getArray(payrollArray).map((pay) => ({
      payroll_employee_name: pay.payroll_employee_name || "Unknown",
      total_hour: pay.total_hour || 0,
      salary_unit: pay.salary_unit || "N/A",
      total_amount: pay.total_amount || "0.00",
      payroll_date: pay.payroll_date || "N/A",
      pay_method: pay.pay_method || "other",
    }));
  };

  const formatGasInvoices = (gasInvoices = []) => {
    return getArray(gasInvoices).map((inv) => ({
      invoiceNumber: inv.invoice_no || "N/A",
      vendorName: inv.vendor_department_name || "Unknown",
      category: "Gas",
      amount: getNumber(inv.invoice_amount || inv.amount),
      type: inv.pay_method || "Cash",
      date: inv.date || "N/A",
      invoice_no: inv.invoice_no || "N/A",
      invoice_amounts: inv.invoice_amount || inv.amount || 0,
    }));
  };

  const transformApiDataToReportFormat = (apiData) => {
    const {
      total_all = 0,
      total_purchase_amount = 0,
      purchase_invoices = { results: [] },
      total_expense_amount = 0,
      expense_invoices = { results: [] },
      total_gas_amount = 0,
      gas_invoices = { results: [] },
      total_payroll = 0,
      payrolls = { results: [] },
    } = apiData || {};

    return {
      debit: {
        total: getNumber(total_all),
        items: [
          {
            title: "Purchases",
            amount: getNumber(total_purchase_amount),
            details: formatInvoices(purchase_invoices, "Purchase"),
          },
          {
            title: "Expenses",
            amount: getNumber(total_expense_amount),
            details: formatInvoices(expense_invoices, "Expense"),
          },
          {
            title: "Gas Purchases",
            amount: getNumber(total_gas_amount),
            details: formatGasInvoices(gas_invoices),
          },
          {
            title: "Payrolls",
            amount: getNumber(total_payroll),
            details: formatPayrolls(payrolls),
          },
        ],
      },
    };
  };

  const transformCreditApiDataToReportFormat = (apiData) => {
    const {
      total_credit_amount = 0,
      total_pos_amount = 0,
      pos_invoices = { results: [] },
      total_quick_sale_amount = 0,
      quick_sale_invoices = { results: [] },
      total_retail_amount = 0,
      retail_invoices = { results: [] },
      total_ecom_amount = 0,
      ecom_invoices = { results: [] },
      total_wholesale_amount = 0,
      wholesale_invoices = { results: [] },
      total_lottery_sales = 0,
      lottery_sale = { results: [] },
      total_credit_invoice = 0,
      filtered_credit_invoice = { results: [] },
    } = apiData || {};

    return {
      credit: {
        total: getNumber(total_credit_amount),
        items: [
          {
            title: "POS App Sales",
            amount: getNumber(total_pos_amount),
            details: getArray(pos_invoices),
          },
          {
            title: "Quick Sales",
            amount: getNumber(total_quick_sale_amount),
            details: getArray(quick_sale_invoices),
          },
          {
            title: "Retail Sales",
            amount: getNumber(total_retail_amount),
            details: getArray(retail_invoices),
          },
          {
            title: "Ecom Sales",
            amount: getNumber(total_ecom_amount),
            details: getArray(ecom_invoices),
          },
          {
            title: "Wholesale Sales",
            amount: getNumber(total_wholesale_amount),
            details: getArray(wholesale_invoices),
          },
          {
            title: "Lottery Sales",
            amount: getNumber(total_lottery_sales),
            details: getArray(lottery_sale),
          },
          {
            title: "Credit Invoices",
            amount: getNumber(total_credit_invoice),
            details: getArray(filtered_credit_invoice),
          },
        ],
      },
    };
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError("");

      const [debitResponse, creditResponse] = await Promise.all([
        Post("debitReport", {
          start_date: startDate,
          end_date: endDate,
        }),
        Post("creditReport", {
          start_date: startDate,
          end_date: endDate,
        }),
      ]);

      setReportData(transformApiDataToReportFormat(debitResponse.data));
      setCreditReportData(transformCreditApiDataToReportFormat(creditResponse.data));
    } catch (fetchError) {
      console.error("Error fetching report data:", fetchError);
      setError(`Failed to fetch report data: ${fetchError.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debitData = reportData || {
    debit: {
      total: 0,
      items: [
        { title: "Purchases", amount: 0, details: [] },
        { title: "Expenses", amount: 0, details: [] },
        { title: "Gas Purchases", amount: 0, details: [] },
        { title: "Payrolls", amount: 0, details: [] },
      ],
    },
  };

  const creditData = creditReportData || {
    credit: {
      total: 0,
      items: [
        { title: "POS App Sales", amount: 0, details: [] },
        { title: "Quick Sales", amount: 0, details: [] },
        { title: "Retail Sales", amount: 0, details: [] },
        { title: "Ecom Sales", amount: 0, details: [] },
        { title: "Wholesale Sales", amount: 0, details: [] },
        { title: "Lottery Sales", amount: 0, details: [] },
        { title: "Credit Invoices", amount: 0, details: [] },
      ],
    },
  };

  const summary = useMemo(() => {
    const debitTotal = getNumber(debitData.debit.total);
    const creditTotal = getNumber(creditData.credit.total);
    const netPosition = creditTotal - debitTotal;
    const profitMargin = creditTotal > 0 ? (netPosition / creditTotal) * 100 : 0;

    const totalCreditSections = creditData.credit.items.filter((item) => getNumber(item.amount) > 0).length;
    const totalDebitSections = debitData.debit.items.filter((item) => getNumber(item.amount) > 0).length;

    return {
      debitTotal,
      creditTotal,
      netPosition,
      profitMargin,
      totalCreditSections,
      totalDebitSections,
    };
  }, [debitData, creditData]);

  const ledgerRows = useMemo(() => {
    const debitRows = debitData.debit.items.flatMap((section) =>
      getArray(section.details).map((detail) => ({
        side: "Debit",
        section: section.title,
        partyRole: section.title === "Payrolls" ? "Employee" : "Vendor",
        invoice: detail.invoice_no || detail.invoiceNumber || detail.id || "N/A",
        party:
          detail.vendorName ||
          detail.vendor_department_name ||
          detail.vendor_name ||
          detail.customer ||
          detail.customer_name ||
          detail.payroll_employee_name ||
          "N/A",
        date: detail.date || detail.payroll_date || "N/A",
        method: detail.pay_method || detail.type || "N/A",
        amount: getDetailAmount(detail),
      }))
    );

    const creditRows = creditData.credit.items.flatMap((section) =>
      getArray(section.details).map((detail) => ({
        side: "Credit",
        section: section.title,
        partyRole: "Customer",
        invoice: detail.invoice_no || detail.invoiceNumber || detail.id || "N/A",
        party:
          detail.customer ||
          detail.customer_name ||
          detail.vendorName ||
          detail.invoice_type ||
          "N/A",
        date: detail.date || detail.payroll_date || "N/A",
        method: detail.pay_method || detail.type || "N/A",
        amount: getDetailAmount(detail),
      }))
    );

    return [...debitRows, ...creditRows];
  }, [debitData, creditData]);

  const auditSummary = useMemo(() => {
    const allDetails = [
      ...debitData.debit.items.flatMap((item) => getArray(item.details).map((detail) => ({ ...detail, side: "debit", section: item.title }))),
      ...creditData.credit.items.flatMap((item) => getArray(item.details).map((detail) => ({ ...detail, side: "credit", section: item.title }))),
    ];

    const paymentTotals = allDetails.reduce(
      (acc, item) => {
        const method = getPaymentMethod(item);
        const amount = getDetailAmount(item);

        if (method.includes("cash")) acc.cash += amount;
        else if (method.includes("cheque")) acc.cheque += amount;
        else if (method.includes("bank") || method.includes("card")) acc.bank += amount;
        else acc.other += amount;

        return acc;
      },
      { cash: 0, bank: 0, cheque: 0, other: 0 }
    );

    const missingDates = allDetails.filter((item) => !item.date && !item.payroll_date).length;
    const missingParties = allDetails.filter(
      (item) =>
        !item.vendorName &&
        !item.vendor_department_name &&
        !item.vendor_name &&
        !item.customer &&
        !item.customer_name &&
        !item.payroll_employee_name
    ).length;
    const zeroAmountRows = allDetails.filter((item) => getDetailAmount(item) === 0).length;
    const transactionCount = allDetails.length;

    const auditFlags = [
      {
        label: "Missing dates",
        value: missingDates,
        status: missingDates === 0 ? "good" : "warn",
      },
      {
        label: "Missing parties",
        value: missingParties,
        status: missingParties === 0 ? "good" : "warn",
      },
      {
        label: "Zero amount rows",
        value: zeroAmountRows,
        status: zeroAmountRows === 0 ? "good" : "warn",
      },
    ];

    return {
      transactionCount,
      paymentTotals,
      auditFlags,
      balanceSheet: [
        { label: "Business Inflow", value: summary.creditTotal, tone: "asset" },
        { label: "Business Outflow", value: summary.debitTotal, tone: "liability" },
        { label: "Net Owner Position", value: summary.netPosition, tone: summary.netPosition >= 0 ? "asset" : "liability" },
      ],
    };
  }, [debitData, creditData, summary.creditTotal, summary.debitTotal, summary.netPosition]);

  const storeInfoRows = useMemo(() => {
    const storeName =
      user?.selected_store_name ||
      user?.store_name ||
      user?.store?.store_name ||
      user?.store?.name ||
      "Store";
    const address = [
      user?.address_line1 || user?.store_address_line1 || user?.store?.address_line1,
      user?.city || user?.store_city || user?.store?.city,
      user?.state || user?.store_state || user?.store?.state,
      user?.pincode || user?.store_pincode || user?.store?.pincode,
      user?.country || user?.store_country || user?.store?.country,
    ].filter(Boolean).join(", ");

    return [
      ["Store Name", storeName],
      ["Store ID", user?.store || user?.store_id || ""],
      ["Store Email", user?.store_email || user?.email || ""],
      ["Store Address", address],
      ["Report Start Date", startDate],
      ["Report End Date", endDate],
      ["Generated At", new Date().toLocaleString()],
    ];
  }, [endDate, startDate, user]);

  const reportTypeLabels = {
    business: "Business Report",
    ledger: "Ledger Report",
    balance_sheet: "Balance Sheet",
    credit_debit: "Credit Debit Statement",
    profit_loss: "Profit & Loss Report",
    cash_flow: "Cash Flow Report",
    tax_summary: "Tax & Payment Summary",
    audit_exceptions: "Audit Exceptions Report",
  };

  const openExportModal = (reportType = "business") => {
    setExportOptions((prev) => ({ ...prev, reportType }));
    setShowExportModal(true);
  };

  const getFilteredLedgerRows = () => {
    const partySearch = exportOptions.partyQuery.trim().toLowerCase();

    return ledgerRows.filter((row) => {
      if (exportOptions.side !== "all" && row.side.toLowerCase() !== exportOptions.side) {
        return false;
      }

      if (exportOptions.partyType !== "all" && row.partyRole.toLowerCase() !== exportOptions.partyType) {
        return false;
      }

      if (partySearch) {
        const searchable = [row.party, row.invoice, row.section, row.method]
          .join(" ")
          .toLowerCase();
        return searchable.includes(partySearch);
      }

      return true;
    });
  };

  const getSectionRows = () => [
    ["Section", "Type", "Amount"],
    ...creditData.credit.items.map((item) => [item.title, "Credit", getNumber(item.amount).toFixed(2)]),
    ...debitData.debit.items.map((item) => [item.title, "Debit", getNumber(item.amount).toFixed(2)]),
  ];

  const getLedgerTableRows = () => [
    ["Side", "Ledger Section", "Party Type", "Invoice/Ref", "Party", "Date", "Payment Method", "Amount"],
    ...getFilteredLedgerRows().map((row) => [
      row.side,
      row.section,
      row.partyRole,
      row.invoice,
      row.party,
      row.date,
      row.method,
      row.amount.toFixed(2),
    ]),
  ];

  const buildLedgerCsvRows = () => {
    return [
      ["Report", reportTypeLabels.ledger],
      ...storeInfoRows,
      ["Party Type", exportOptions.partyType],
      ["Party Search", exportOptions.partyQuery || "All"],
      ["Side", exportOptions.side],
      [],
      ...getLedgerTableRows(),
    ];
  };

  const buildBusinessCsvRows = () => [
    ["Report", reportTypeLabels.business],
    ...storeInfoRows,
    [],
    ["Summary"],
    ["Total Credit", summary.creditTotal.toFixed(2)],
    ["Total Debit", summary.debitTotal.toFixed(2)],
    ["Net Position", summary.netPosition.toFixed(2)],
    ["Margin Indicator", `${summary.profitMargin.toFixed(2)}%`],
    ["Transaction Lines", auditSummary.transactionCount],
    [],
    ...getSectionRows(),
    ...(exportOptions.includeDetails ? [[], ...getLedgerTableRows()] : []),
  ];

  const buildBalanceSheetCsvRows = () => [
    ["Report", reportTypeLabels.balance_sheet],
    ...storeInfoRows,
    [],
    ["Balance Sheet Snapshot"],
    ["Line", "Amount"],
    ...auditSummary.balanceSheet.map((item) => [item.label, item.value.toFixed(2)]),
    [],
    ["Payment Mix"],
    ["Cash", auditSummary.paymentTotals.cash.toFixed(2)],
    ["Bank/Card", auditSummary.paymentTotals.bank.toFixed(2)],
    ["Cheque", auditSummary.paymentTotals.cheque.toFixed(2)],
    ["Other", auditSummary.paymentTotals.other.toFixed(2)],
  ];

  const buildCreditDebitCsvRows = () => [
    ["Report", reportTypeLabels.credit_debit],
    ...storeInfoRows,
    [],
    ["Statement"],
    ["Credit Total", summary.creditTotal.toFixed(2)],
    ["Debit Total", summary.debitTotal.toFixed(2)],
    ["Net Position", summary.netPosition.toFixed(2)],
    [],
    ...getSectionRows(),
    ...(exportOptions.includeDetails ? [[], ...getLedgerTableRows()] : []),
  ];

  const buildProfitLossCsvRows = () => [
    ["Report", reportTypeLabels.profit_loss],
    ...storeInfoRows,
    [],
    ["Profit & Loss"],
    ["Income / Credit", summary.creditTotal.toFixed(2)],
    ["Cost / Expense / Debit", summary.debitTotal.toFixed(2)],
    ["Net Profit / Loss", summary.netPosition.toFixed(2)],
    ["Profit Margin", `${summary.profitMargin.toFixed(2)}%`],
    [],
    ["Income Breakdown"],
    ["Section", "Amount"],
    ...creditData.credit.items.map((item) => [item.title, getNumber(item.amount).toFixed(2)]),
    [],
    ["Expense Breakdown"],
    ["Section", "Amount"],
    ...debitData.debit.items.map((item) => [item.title, getNumber(item.amount).toFixed(2)]),
    ...(exportOptions.includeDetails ? [[], ...getLedgerTableRows()] : []),
  ];

  const buildCashFlowCsvRows = () => [
    ["Report", reportTypeLabels.cash_flow],
    ...storeInfoRows,
    [],
    ["Cash Flow / Payment Movement"],
    ["Payment Type", "Amount"],
    ["Cash", auditSummary.paymentTotals.cash.toFixed(2)],
    ["Bank/Card", auditSummary.paymentTotals.bank.toFixed(2)],
    ["Cheque", auditSummary.paymentTotals.cheque.toFixed(2)],
    ["Other", auditSummary.paymentTotals.other.toFixed(2)],
    [],
    ["Net Cash Position", summary.netPosition.toFixed(2)],
    ...(exportOptions.includeDetails ? [[], ...getLedgerTableRows()] : []),
  ];

  const buildTaxSummaryCsvRows = () => [
    ["Report", reportTypeLabels.tax_summary],
    ...storeInfoRows,
    [],
    ["Tax & Payment Summary"],
    ["Total Sales / Credit", summary.creditTotal.toFixed(2)],
    ["Total Purchases / Debit", summary.debitTotal.toFixed(2)],
    ["Cash Collected/Paid", auditSummary.paymentTotals.cash.toFixed(2)],
    ["Bank/Card Collected/Paid", auditSummary.paymentTotals.bank.toFixed(2)],
    ["Cheque Collected/Paid", auditSummary.paymentTotals.cheque.toFixed(2)],
    ["Other Collected/Paid", auditSummary.paymentTotals.other.toFixed(2)],
    [],
    ...getSectionRows(),
  ];

  const buildAuditExceptionsCsvRows = () => [
    ["Report", reportTypeLabels.audit_exceptions],
    ...storeInfoRows,
    [],
    ["Audit Checks"],
    ["Check", "Count", "Status"],
    ...auditSummary.auditFlags.map((flag) => [flag.label, flag.value, flag.status]),
    [],
    ["Rows Needing Review"],
    ["Side", "Ledger Section", "Party Type", "Invoice/Ref", "Party", "Date", "Payment Method", "Amount", "Reason"],
    ...getFilteredLedgerRows()
      .map((row) => ({
        ...row,
        reasons: [
          !row.date || row.date === "N/A" ? "Missing date" : "",
          !row.party || row.party === "N/A" ? "Missing party" : "",
          getNumber(row.amount) === 0 ? "Zero amount" : "",
        ].filter(Boolean),
      }))
      .filter((row) => row.reasons.length > 0)
      .map((row) => [
        row.side,
        row.section,
        row.partyRole,
        row.invoice,
        row.party,
        row.date,
        row.method,
        row.amount.toFixed(2),
        row.reasons.join("; "),
      ]),
  ];

  const handleGenerateExport = () => {
    const builders = {
      business: buildBusinessCsvRows,
      ledger: buildLedgerCsvRows,
      balance_sheet: buildBalanceSheetCsvRows,
      credit_debit: buildCreditDebitCsvRows,
      profit_loss: buildProfitLossCsvRows,
      cash_flow: buildCashFlowCsvRows,
      tax_summary: buildTaxSummaryCsvRows,
      audit_exceptions: buildAuditExceptionsCsvRows,
    };
    const rows = builders[exportOptions.reportType]();
    const filenameType = exportOptions.reportType.replace(/_/g, "-");

    downloadCsv(`${filenameType}-${startDate}-to-${endDate}.csv`, rows);
    setShowExportModal(false);
  };

  const getCreditDetailComponent = (title, details) => {
    switch (title) {
      case "POS App Sales":
        return <POSAppInvoiceDetails data={details} />;
      case "Quick Sales":
        return <SalesDetails data={details} type="Quick Sales" />;
      case "Retail Sales":
        return <SalesDetails data={details} type="Retail Sales" />;
      case "Ecom Sales":
        return <SalesDetails data={details} type="Ecom Sales" />;
      case "Wholesale Sales":
        return <SalesDetails data={details} type="Wholesale Sales" />;
      case "Lottery Sales":
        return <LotterySalesDetails data={details} />;
      case "Credit Invoices":
        return <CreditInvoiceDetails data={details} />;
      default:
        return <NoRecords />;
    }
  };

  const getDebitDetailComponent = (title, details) => {
    switch (title) {
      case "Purchases":
      case "Expenses":
        return <DebitDetails data={details} />;
      case "Gas Purchases":
        return <GasPurchase data={details} />;
      case "Payrolls":
        return <PayrollDetails data={details} />;
      default:
        return <NoRecords />;
    }
  };

  const renderKpiCard = ({ title, value, subtitle, type, icon }) => (
    <div className={`pvr-kpi-card pvr-kpi-${type}`}>
      <div className="pvr-kpi-top">
        <span className="pvr-kpi-icon">{icon}</span>
        <span>{title}</span>
      </div>
      <div className="pvr-kpi-value">{value}</div>
      <div className="pvr-kpi-subtitle">{subtitle}</div>
    </div>
  );

  if (loading) {
    return <LoadingOverlay loading={loading} loader={loader} />;
  }

  if (error) {
    return (
      <div className="responsive-box">
        <div className="pvr-error-card">
          <h3>Unable to load report</h3>
          <p>{error}</p>
          <button type="button" onClick={fetchReportData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="responsive-box pvr-report-page">
      <style>
        {`
          .pvr-report-page {
            background:
              radial-gradient(circle at top left, rgba(16, 185, 129, 0.12), transparent 34%),
              radial-gradient(circle at top right, rgba(99, 102, 241, 0.10), transparent 34%),
              linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
            min-height: 100vh;
            padding-top: 6px;
            padding-bottom: 24px;
          }

          .pvr-hero {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 58%, #115e59 100%);
            color: #ffffff;
            border-radius: 28px;
            padding: 26px;
            box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
            margin-bottom: 18px;
            position: relative;
            overflow: hidden;
          }

          .pvr-hero::after {
            content: "";
            position: absolute;
            width: 280px;
            height: 280px;
            right: -110px;
            top: -120px;
            border-radius: 50%;
            background: rgba(45, 212, 191, 0.17);
          }

          .pvr-hero-content {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 22px;
            flex-wrap: wrap;
          }

          .pvr-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255,255,255,0.13);
            border: 1px solid rgba(255,255,255,0.24);
            color: #ccfbf1;
            padding: 8px 13px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 950;
            margin-bottom: 13px;
          }

          .pvr-title {
            margin: 0;
            font-size: 32px;
            font-weight: 950;
            letter-spacing: -0.04em;
            line-height: 1.1;
          }

          .pvr-description {
            margin: 9px 0 0;
            max-width: 700px;
            color: rgba(255,255,255,0.82);
            font-size: 14px;
            line-height: 1.6;
          }

          .pvr-filter-card {
            background: rgba(255,255,255,0.12);
            border: 1px solid rgba(255,255,255,0.24);
            border-radius: 18px;
            padding: 14px;
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
            align-items: flex-end;
          }

          .pvr-filter-group {
            display: flex;
            flex-direction: column;
            gap: 5px;
          }

          .pvr-filter-group label {
            font-size: 11px;
            font-weight: 900;
            color: rgba(255,255,255,0.72);
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .pvr-filter-group input {
            height: 38px;
            border: none;
            outline: none;
            border-radius: 12px;
            padding: 0 11px;
            font-size: 13px;
            font-weight: 800;
          }

          .pvr-refresh-btn {
            height: 38px;
            border: none;
            border-radius: 12px;
            padding: 0 14px;
            font-size: 13px;
            font-weight: 950;
            background: #ffffff;
            color: #0f172a;
            cursor: pointer;
          }

          .pvr-export-btn {
            height: 38px;
            border: 1px solid rgba(255,255,255,0.3);
            border-radius: 12px;
            padding: 0 14px;
            font-size: 13px;
            font-weight: 950;
            background: #14b8a6;
            color: #ffffff;
            cursor: pointer;
            box-shadow: 0 10px 24px rgba(20, 184, 166, 0.22);
          }

          .pvr-export-chip {
            height: 38px;
            border: 1px solid rgba(255,255,255,0.24);
            border-radius: 12px;
            padding: 0 12px;
            font-size: 12px;
            font-weight: 950;
            background: rgba(255,255,255,0.12);
            color: #ffffff;
            cursor: pointer;
          }

          .pvr-kpi-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 14px;
            margin-bottom: 18px;
          }

          .pvr-kpi-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 22px;
            padding: 17px 18px;
            box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
          }

          .pvr-kpi-credit {
            background: linear-gradient(180deg, #ecfdf5 0%, #ffffff 100%);
            border-color: #bbf7d0;
          }

          .pvr-kpi-debit {
            background: linear-gradient(180deg, #fef2f2 0%, #ffffff 100%);
            border-color: #fecaca;
          }

          .pvr-kpi-net-positive {
            background: linear-gradient(180deg, #eef2ff 0%, #ffffff 100%);
            border-color: #c7d2fe;
          }

          .pvr-kpi-net-negative {
            background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
            border-color: #fed7aa;
          }

          .pvr-kpi-top {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 11px;
            color: #64748b;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.055em;
          }

          .pvr-kpi-icon {
            width: 28px;
            height: 28px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
            font-size: 15px;
          }

          .pvr-kpi-value {
            margin-top: 10px;
            font-size: 25px;
            font-weight: 950;
            color: #0f172a;
            line-height: 1;
            word-break: break-word;
          }

          .pvr-kpi-subtitle {
            margin-top: 8px;
            font-size: 12px;
            color: #64748b;
            font-weight: 750;
          }

          .pvr-audit-grid {
            display: grid;
            grid-template-columns: 1.1fr 1fr 1fr;
            gap: 14px;
            margin-bottom: 18px;
          }

          .pvr-audit-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 22px;
            padding: 17px;
            box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
          }

          .pvr-audit-title {
            margin: 0 0 4px;
            color: #0f172a;
            font-size: 17px;
            font-weight: 950;
          }

          .pvr-audit-subtitle {
            margin: 0 0 14px;
            color: #64748b;
            font-size: 12px;
            font-weight: 750;
            line-height: 1.45;
          }

          .pvr-balance-row,
          .pvr-payment-row,
          .pvr-check-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            padding: 11px 0;
            border-bottom: 1px solid #f1f5f9;
            color: #334155;
            font-size: 13px;
            font-weight: 850;
          }

          .pvr-balance-row:last-child,
          .pvr-payment-row:last-child,
          .pvr-check-row:last-child {
            border-bottom: none;
          }

          .pvr-balance-value {
            font-weight: 950;
            color: #0f172a;
            white-space: nowrap;
          }

          .pvr-balance-value.asset {
            color: #047857;
          }

          .pvr-balance-value.liability {
            color: #b91c1c;
          }

          .pvr-payment-bar {
            height: 8px;
            background: #e5e7eb;
            border-radius: 999px;
            overflow: hidden;
            margin-top: 12px;
            display: flex;
          }

          .pvr-payment-segment.cash {
            background: #10b981;
          }

          .pvr-payment-segment.bank {
            background: #3b82f6;
          }

          .pvr-payment-segment.cheque {
            background: #f97316;
          }

          .pvr-payment-segment.other {
            background: #8b5cf6;
          }

          .pvr-check-pill {
            border-radius: 999px;
            padding: 6px 10px;
            font-size: 12px;
            font-weight: 950;
          }

          .pvr-check-pill.good {
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #a7f3d0;
          }

          .pvr-check-pill.warn {
            background: #fff7ed;
            color: #c2410c;
            border: 1px solid #fed7aa;
          }

          .pvr-export-overlay {
            position: fixed;
            inset: 0;
            z-index: 1050;
            background: rgba(15, 23, 42, 0.52);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 18px;
          }

          .pvr-export-modal {
            width: min(820px, 100%);
            max-height: min(88vh, 780px);
            overflow: auto;
            background: #ffffff;
            border-radius: 22px;
            border: 1px solid #e5e7eb;
            box-shadow: 0 30px 80px rgba(15, 23, 42, 0.32);
          }

          .pvr-export-modal-head,
          .pvr-export-modal-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 14px;
            padding: 18px 20px;
          }

          .pvr-export-modal-head {
            border-bottom: 1px solid #e5e7eb;
          }

          .pvr-export-modal-actions {
            justify-content: flex-end;
            border-top: 1px solid #e5e7eb;
            flex-wrap: wrap;
          }

          .pvr-export-modal-title {
            margin: 0;
            color: #0f172a;
            font-size: 20px;
            font-weight: 950;
          }

          .pvr-export-modal-subtitle {
            margin: 4px 0 0;
            color: #64748b;
            font-size: 13px;
            font-weight: 750;
          }

          .pvr-export-close {
            width: 38px;
            height: 38px;
            border: none;
            border-radius: 12px;
            background: #f1f5f9;
            color: #334155;
            cursor: pointer;
            font-size: 22px;
          }

          .pvr-export-modal-body {
            padding: 20px;
            display: grid;
            gap: 18px;
          }

          .pvr-export-section-title {
            margin: 0 0 10px;
            color: #334155;
            font-size: 12px;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .pvr-export-type-grid,
          .pvr-export-filter-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 10px;
          }

          .pvr-export-type {
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            background: #ffffff;
            color: #334155;
            padding: 12px;
            text-align: left;
            font-weight: 950;
            cursor: pointer;
          }

          .pvr-export-type.active {
            border-color: #14b8a6;
            background: #f0fdfa;
            color: #0f766e;
          }

          .pvr-export-field label,
          .pvr-export-toggle {
            display: block;
            color: #64748b;
            font-size: 12px;
            font-weight: 900;
            margin-bottom: 6px;
          }

          .pvr-export-field input,
          .pvr-export-field select {
            width: 100%;
            height: 42px;
            border: 1px solid #d1d5db;
            border-radius: 12px;
            padding: 0 12px;
            color: #0f172a;
            font-weight: 850;
            background: #ffffff;
          }

          .pvr-store-preview {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 13px;
            color: #475569;
            font-size: 12px;
            font-weight: 800;
            line-height: 1.6;
          }

          .pvr-modal-btn {
            border: none;
            border-radius: 999px;
            padding: 10px 16px;
            font-weight: 950;
            cursor: pointer;
          }

          .pvr-modal-btn.light {
            background: #f1f5f9;
            color: #334155;
          }

          .pvr-modal-btn.primary {
            background: #0f766e;
            color: #ffffff;
          }

          .pvr-graphs-wrapper {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 16px;
            margin-bottom: 18px;
          }

          .pvr-graph-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 22px;
            padding: 16px;
            box-shadow: 0 14px 36px rgba(15, 23, 42, 0.06);
          }

          .pvr-graph-title {
            font-size: 15px;
            font-weight: 950;
            color: #0f172a;
            margin-bottom: 10px;
          }

          .pvr-ledger-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 18px;
          }

          .pvr-ledger-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 24px;
            padding: 18px;
            box-shadow: 0 16px 42px rgba(15, 23, 42, 0.07);
          }

          .pvr-ledger-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 14px;
          }

          .pvr-ledger-title {
            margin: 0;
            font-size: 21px;
            font-weight: 950;
            color: #0f172a;
          }

          .pvr-ledger-subtitle {
            margin: 5px 0 0;
            font-size: 12px;
            color: #64748b;
            font-weight: 750;
          }

          .pvr-ledger-total {
            border-radius: 16px;
            padding: 11px 13px;
            font-size: 13px;
            font-weight: 950;
            white-space: nowrap;
          }

          .pvr-ledger-total.debit {
            color: #b91c1c;
            background: #fef2f2;
            border: 1px solid #fecaca;
          }

          .pvr-ledger-total.credit {
            color: #047857;
            background: #ecfdf5;
            border: 1px solid #a7f3d0;
          }

          .pvr-collapsible-item {
            border: 1px solid #e5e7eb;
            border-radius: 18px;
            overflow: hidden;
            margin-bottom: 12px;
            background: #ffffff;
          }

          .pvr-collapsible-trigger {
            width: 100%;
            border: none;
            background: #ffffff;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 14px;
            padding: 15px 16px;
            cursor: pointer;
            text-align: left;
          }

          .pvr-collapsible-trigger.pvr-debit {
            background: linear-gradient(90deg, #fff7f7 0%, #ffffff 100%);
          }

          .pvr-collapsible-trigger.pvr-credit {
            background: linear-gradient(90deg, #f0fdf4 0%, #ffffff 100%);
          }

          .pvr-collapsible-title {
            font-size: 14px;
            font-weight: 950;
            color: #0f172a;
          }

          .pvr-collapsible-subtitle {
            margin-top: 3px;
            font-size: 11px;
            color: #64748b;
            font-weight: 700;
          }

          .pvr-collapsible-amount {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 14px;
            font-weight: 950;
            color: #0f172a;
            white-space: nowrap;
          }

          .pvr-collapsible-icon {
            width: 26px;
            height: 26px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #f1f5f9;
            color: #0f172a;
            font-size: 18px;
            line-height: 1;
          }

          .pvr-collapsible-body {
            border-top: 1px solid #e5e7eb;
            padding: 14px;
            background: #f8fafc;
          }

          .pvr-details-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 16px;
            padding: 12px;
          }

          .pvr-payment-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 12px;
          }

          .pvr-payment-tag {
            border-radius: 999px;
            padding: 7px 10px;
            font-size: 11px;
            font-weight: 950;
          }

          .pvr-cash {
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #a7f3d0;
          }

          .pvr-cheque {
            background: #fff7ed;
            color: #c2410c;
            border: 1px solid #fed7aa;
          }

          .pvr-bank {
            background: #eff6ff;
            color: #1d4ed8;
            border: 1px solid #bfdbfe;
          }

          .pvr-other {
            background: #f5f3ff;
            color: #6d28d9;
            border: 1px solid #ddd6fe;
          }

          .pvr-detail-table-wrap {
            overflow-x: auto;
            border: 1px solid #e5e7eb;
            border-radius: 14px;
          }

          .pvr-detail-table {
            width: 100%;
            border-collapse: collapse;
            background: #ffffff;
          }

          .pvr-detail-table th {
            background: #f8fafc;
            color: #475569;
            font-size: 11px;
            font-weight: 950;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            padding: 10px;
            border-bottom: 1px solid #e5e7eb;
            text-align: left;
            white-space: nowrap;
          }

          .pvr-detail-table td {
            padding: 10px;
            border-bottom: 1px solid #f1f5f9;
            color: #0f172a;
            font-size: 12px;
            font-weight: 700;
            vertical-align: top;
          }

          .pvr-empty-state {
            background: #f8fafc;
            border: 1px dashed #cbd5e1;
            border-radius: 14px;
            padding: 18px;
            color: #64748b;
            font-size: 13px;
            font-weight: 800;
            text-align: center;
          }

          .pvr-error-card {
            background: #ffffff;
            border: 1px solid #fecaca;
            border-radius: 20px;
            padding: 22px;
            color: #991b1b;
          }

          .pvr-error-card button {
            background: #991b1b;
            color: #ffffff;
            border: none;
            border-radius: 999px;
            padding: 10px 16px;
            font-weight: 900;
            cursor: pointer;
          }

          @media (max-width: 1200px) {
            .pvr-kpi-grid,
            .pvr-graphs-wrapper {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .pvr-ledger-grid {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 768px) {
            .pvr-hero {
              border-radius: 20px;
              padding: 20px;
            }

            .pvr-title {
              font-size: 25px;
            }

            .pvr-filter-card {
              width: 100%;
            }

            .pvr-filter-group,
            .pvr-filter-group input,
            .pvr-refresh-btn,
            .pvr-export-btn,
            .pvr-export-chip {
              width: 100%;
            }

            .pvr-kpi-grid,
            .pvr-graphs-wrapper,
            .pvr-audit-grid {
              grid-template-columns: 1fr;
            }

            .pvr-ledger-header {
              flex-direction: column;
            }

            .pvr-ledger-total {
              width: 100%;
              text-align: center;
            }

            .pvr-collapsible-trigger {
              align-items: flex-start;
              flex-direction: column;
            }

            .pvr-collapsible-amount {
              width: 100%;
              justify-content: space-between;
            }

            .pvr-export-type-grid,
            .pvr-export-filter-grid {
              grid-template-columns: 1fr;
            }

            .pvr-export-modal-actions .pvr-modal-btn {
              width: 100%;
            }
          }

          @media (max-width: 1200px) and (min-width: 769px) {
            .pvr-audit-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }
        `}
      </style>

      <div className="pvr-hero">
        <div className="pvr-hero-content">
          <div>
            <div className="pvr-badge">
              <span>●</span>
              <span>Business Performance Report</span>
            </div>

            <h2 className="pvr-title">Debit & Credit Dashboard</h2>

            <p className="pvr-description">
              Monitor money going out and money coming in across purchases, expenses, payroll, retail, wholesale, lottery, quick sales and POS App transactions.
            </p>
          </div>

          <div className="pvr-filter-card">
            <div className="pvr-filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="pvr-filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <button type="button" className="pvr-refresh-btn" onClick={fetchReportData}>
              Refresh Report
            </button>

            <button type="button" className="pvr-export-btn" onClick={() => openExportModal("business")}>
              Export Report
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("ledger")}>
              Ledger
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("business")}>
              Business Report
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("balance_sheet")}>
              Balance Sheet
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("credit_debit")}>
              Credit/Debit
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("profit_loss")}>
              Profit & Loss
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("cash_flow")}>
              Cash Flow
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("tax_summary")}>
              Tax Summary
            </button>

            <button type="button" className="pvr-export-chip" onClick={() => openExportModal("audit_exceptions")}>
              Audit Check
            </button>
          </div>
        </div>
      </div>

      <div className="pvr-kpi-grid">
        {renderKpiCard({
          title: "Total Credit",
          value: formatCurrency(summary.creditTotal.toFixed(2)),
          subtitle: `${summary.totalCreditSections} active income categories`,
          type: "credit",
          icon: "↗",
        })}

        {renderKpiCard({
          title: "Total Debit",
          value: formatCurrency(summary.debitTotal.toFixed(2)),
          subtitle: `${summary.totalDebitSections} active expense categories`,
          type: "debit",
          icon: "↘",
        })}

        {renderKpiCard({
          title: "Net Position",
          value: formatCurrency(summary.netPosition.toFixed(2)),
          subtitle: summary.netPosition >= 0 ? "Credit is higher than debit" : "Debit is higher than credit",
          type: summary.netPosition >= 0 ? "net-positive" : "net-negative",
          icon: "⇄",
        })}

        {renderKpiCard({
          title: "Margin Indicator",
          value: `${summary.profitMargin.toFixed(2)}%`,
          subtitle: "Net position compared to credit",
          type: summary.profitMargin >= 0 ? "net-positive" : "net-negative",
          icon: "%",
        })}
      </div>

      <div className="pvr-audit-grid">
        <div className="pvr-audit-card">
          <h3 className="pvr-audit-title">Balance Sheet Snapshot</h3>
          <p className="pvr-audit-subtitle">
            Quick assets-versus-outflow view for the selected reporting period.
          </p>

          {auditSummary.balanceSheet.map((item) => (
            <div className="pvr-balance-row" key={item.label}>
              <span>{item.label}</span>
              <span className={`pvr-balance-value ${item.tone}`}>
                {formatCurrency(item.value.toFixed(2))}
              </span>
            </div>
          ))}
        </div>

        <div className="pvr-audit-card">
          <h3 className="pvr-audit-title">Ledger & Cash Flow</h3>
          <p className="pvr-audit-subtitle">
            Payment mix and transaction volume for audit reconciliation.
          </p>

          {[
            ["Cash", auditSummary.paymentTotals.cash, "cash"],
            ["Bank/Card", auditSummary.paymentTotals.bank, "bank"],
            ["Cheque", auditSummary.paymentTotals.cheque, "cheque"],
            ["Other", auditSummary.paymentTotals.other, "other"],
          ].map(([label, value]) => (
            <div className="pvr-payment-row" key={label}>
              <span>{label}</span>
              <strong>{formatCurrency(value.toFixed(2))}</strong>
            </div>
          ))}

          <div className="pvr-payment-bar" aria-label="Payment mix">
            {[
              ["cash", auditSummary.paymentTotals.cash],
              ["bank", auditSummary.paymentTotals.bank],
              ["cheque", auditSummary.paymentTotals.cheque],
              ["other", auditSummary.paymentTotals.other],
            ].map(([type, value]) => {
              const totalPayments =
                auditSummary.paymentTotals.cash +
                auditSummary.paymentTotals.bank +
                auditSummary.paymentTotals.cheque +
                auditSummary.paymentTotals.other;
              const width = totalPayments > 0 ? (value / totalPayments) * 100 : 0;
              return (
                <span
                  key={type}
                  className={`pvr-payment-segment ${type}`}
                  style={{ width: `${width}%` }}
                />
              );
            })}
          </div>
        </div>

        <div className="pvr-audit-card">
          <h3 className="pvr-audit-title">Audit Readiness</h3>
          <p className="pvr-audit-subtitle">
            Checks that help find incomplete records before accountant review.
          </p>

          <div className="pvr-check-row">
            <span>Ledger lines</span>
            <span className="pvr-check-pill good">{auditSummary.transactionCount}</span>
          </div>

          {auditSummary.auditFlags.map((flag) => (
            <div className="pvr-check-row" key={flag.label}>
              <span>{flag.label}</span>
              <span className={`pvr-check-pill ${flag.status}`}>{flag.value}</span>
            </div>
          ))}
        </div>
      </div>

      <ReportGraphs debitData={debitData} creditData={creditData} />

      <div className="pvr-ledger-grid">
        <div className="pvr-ledger-card">
          <div className="pvr-ledger-header">
            <div>
              <h2 className="pvr-ledger-title">Debit</h2>
              <p className="pvr-ledger-subtitle">Money going out of the business.</p>
            </div>

            <div className="pvr-ledger-total debit">
              Total: {formatCurrency(summary.debitTotal.toFixed(2))}
            </div>
          </div>

          {debitData.debit.items.map((item, index) => (
            <CollapsibleItem
              key={`${item.title}-${index}`}
              title={item.title}
              amount={item.amount}
              type="debit"
              details={getDebitDetailComponent(item.title, item.details)}
            />
          ))}
        </div>

        <div className="pvr-ledger-card">
          <div className="pvr-ledger-header">
            <div>
              <h2 className="pvr-ledger-title">Credit</h2>
              <p className="pvr-ledger-subtitle">Money received and sales income.</p>
            </div>

            <div className="pvr-ledger-total credit">
              Total: {formatCurrency(summary.creditTotal.toFixed(2))}
            </div>
          </div>

          {creditData.credit.items.map((item, index) => (
            <CollapsibleItem
              key={`${item.title}-${index}`}
              title={item.title}
              amount={item.amount}
              type="credit"
              details={getCreditDetailComponent(item.title, item.details)}
            />
          ))}
        </div>
      </div>

      {showExportModal && (
        <div className="pvr-export-overlay" role="dialog" aria-modal="true">
          <div className="pvr-export-modal">
            <div className="pvr-export-modal-head">
              <div>
                <h3 className="pvr-export-modal-title">Export Accountant Report</h3>
                <p className="pvr-export-modal-subtitle">
                  Select report type, ledger filters, and details to include in the CSV.
                </p>
              </div>

              <button
                type="button"
                className="pvr-export-close"
                onClick={() => setShowExportModal(false)}
                aria-label="Close export popup"
              >
                ×
              </button>
            </div>

            <div className="pvr-export-modal-body">
              <section>
                <h4 className="pvr-export-section-title">Report Type</h4>
                <div className="pvr-export-type-grid">
                  {[
                    ["business", "Business Report"],
                    ["ledger", "Ledger"],
                    ["balance_sheet", "Balance Sheet"],
                    ["credit_debit", "Credit/Debit Statement"],
                    ["profit_loss", "Profit & Loss"],
                    ["cash_flow", "Cash Flow"],
                    ["tax_summary", "Tax / Payment Summary"],
                    ["audit_exceptions", "Audit Exceptions"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`pvr-export-type ${exportOptions.reportType === value ? "active" : ""}`}
                      onClick={() => setExportOptions((prev) => ({ ...prev, reportType: value }))}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <h4 className="pvr-export-section-title">Ledger Conditions</h4>
                <div className="pvr-export-filter-grid">
                  <div className="pvr-export-field">
                    <label>Party type</label>
                    <select
                      value={exportOptions.partyType}
                      onChange={(event) =>
                        setExportOptions((prev) => ({ ...prev, partyType: event.target.value }))
                      }
                    >
                      <option value="all">All parties</option>
                      <option value="customer">Customer only</option>
                      <option value="vendor">Vendor only</option>
                      <option value="employee">Employee only</option>
                    </select>
                  </div>

                  <div className="pvr-export-field">
                    <label>Customer / vendor name</label>
                    <input
                      type="text"
                      value={exportOptions.partyQuery}
                      onChange={(event) =>
                        setExportOptions((prev) => ({ ...prev, partyQuery: event.target.value }))
                      }
                      placeholder="Leave blank for all"
                    />
                  </div>

                  <div className="pvr-export-field">
                    <label>Statement side</label>
                    <select
                      value={exportOptions.side}
                      onChange={(event) =>
                        setExportOptions((prev) => ({ ...prev, side: event.target.value }))
                      }
                    >
                      <option value="all">Credit and debit</option>
                      <option value="credit">Credit only</option>
                      <option value="debit">Debit only</option>
                    </select>
                  </div>

                  <label className="pvr-export-toggle">
                    <span>Include details</span>
                    <input
                      type="checkbox"
                      checked={exportOptions.includeDetails}
                      onChange={(event) =>
                        setExportOptions((prev) => ({ ...prev, includeDetails: event.target.checked }))
                      }
                      style={{ marginLeft: 8 }}
                    />
                  </label>
                </div>
              </section>

              <section>
                <h4 className="pvr-export-section-title">Store Information Included</h4>
                <div className="pvr-store-preview">
                  {storeInfoRows.map(([label, value]) => (
                    <div key={label}>
                      <strong>{label}:</strong> {value || "N/A"}
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="pvr-export-modal-actions">
              <button
                type="button"
                className="pvr-modal-btn light"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </button>
              <button type="button" className="pvr-modal-btn primary" onClick={handleGenerateExport}>
                Generate CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportComponent;
